import * as THREE from 'three'
import { MeshBVH } from 'three-mesh-bvh'
import type {
  NormalizedSpatialArtifact,
  SpatialSerializedBVH,
} from '@/plugins/spatial/normalizedSpatialArtifact'
import type {
  SpatialHitMetadata,
  SpatialRepresentationMetadataSnapshot,
  SpatialRepresentationSourceSnapshot,
} from '@/shared/types/spatial'

// 几何归一化、BVH 依赖版本或构建参数变化时必须更新此格式标识。
const FORMAT = 'share3d-spatial-1/bvh-0.9.9/indirect-center-leaf10'
const MAGIC = 0x53425648

interface ArtifactHeader {
  format: string
  lengths: number[]
  groups: THREE.BufferGeometry['groups']
  metadata: Omit<SpatialRepresentationMetadataSnapshot, 'source'>
  hitTable: SpatialHitMetadata[]
}

/** 导出独立二进制工件，不引用活动几何体或 SharedArrayBuffer。 */
export function encodeSpatialArtifact(
  artifact: NormalizedSpatialArtifact,
  geometry: THREE.BufferGeometry,
  bvh: MeshBVH
): Uint8Array {
  const serialized = MeshBVH.serialize(bvh, { cloneBuffers: false }) as SpatialSerializedBVH
  const index = geometry.index
    ? Uint32Array.from(geometry.index.array)
    : Uint32Array.from({ length: geometry.getAttribute('position').count }, (_, i) => i)
  if (!serialized.indirectBuffer) {
    throw new Error('Spatial artifact requires indirect BVH')
  }
  const hitTable: SpatialHitMetadata[] = []
  const hitIds = new Map<string, number>()
  const triangleHits = new Uint32Array(artifact.triangleMetadata.length)
  artifact.triangleMetadata.forEach((hit, face) => {
    const key = JSON.stringify([hit.meshId, hit.nodeId, hit.materialIndex, hit.materialName])
    let id = hitIds.get(key)
    if (id === undefined) {
      id = hitTable.length
      hitIds.set(key, id)
      hitTable.push(hit)
    }
    triangleHits[face] = id
  })
  const sections = [
    copyAttribute(geometry, 'position'),
    copyAttribute(geometry, 'normal'),
    index,
    Uint32Array.from(serialized.indirectBuffer),
    triangleHits,
    ...serialized.roots.map((root) => new Uint8Array(root)),
  ].map((array) => new Uint8Array(array.buffer, array.byteOffset, array.byteLength))
  const header: ArtifactHeader = {
    format: FORMAT,
    lengths: sections.map((section) => section.byteLength),
    groups: geometry.groups,
    metadata: {
      meshMetadata: artifact.metadata.meshMetadata,
      semanticTags: artifact.metadata.semanticTags,
    },
    hitTable,
  }
  const json = new TextEncoder().encode(JSON.stringify(header))
  const payloadOffset = 8 + Math.ceil(json.length / 4) * 4
  const output = new Uint8Array(
    payloadOffset + sections.reduce((sum, item) => sum + item.length, 0)
  )
  const view = new DataView(output.buffer)
  view.setUint32(0, MAGIC, true)
  view.setUint32(4, json.length, true)
  output.set(json, 8)
  let offset = payloadOffset
  for (const section of sections) {
    output.set(section, offset)
    offset += section.byteLength
  }
  return output
}

/** 校验并恢复几何体和 BVH 数据；无效工件抛错，由加载流程回退。 */
export function decodeSpatialArtifact(
  input: Uint8Array,
  source: SpatialRepresentationSourceSnapshot
): NormalizedSpatialArtifact {
  if (!(input instanceof Uint8Array) || input.byteLength < 8) {
    throw new Error('Invalid spatial artifact header')
  }
  const view = new DataView(input.buffer, input.byteOffset, input.byteLength)
  const headerLength = view.getUint32(4, true)
  if (view.getUint32(0, true) !== MAGIC || headerLength > input.length - 8) {
    throw new Error('Invalid spatial artifact signature')
  }
  const header: ArtifactHeader = JSON.parse(
    new TextDecoder().decode(input.subarray(8, 8 + headerLength))
  )
  let offset = 8 + Math.ceil(headerLength / 4) * 4
  if (
    header.format !== FORMAT ||
    !Array.isArray(header.lengths) ||
    header.lengths.length < 6 ||
    header.lengths.some(
      (length) => !Number.isSafeInteger(length) || length <= 0 || length % 4 !== 0
    ) ||
    offset + header.lengths.reduce((sum, length) => sum + length, 0) !== input.length
  ) {
    throw new Error('Incompatible spatial artifact format or length')
  }
  const sections = header.lengths.map((length) => {
    const bytes = new Uint8Array(length)
    bytes.set(input.subarray(offset, offset + length))
    offset += length
    return bytes.buffer
  })
  const positions = new Float32Array(sections[0])
  const normals = new Float32Array(sections[1])
  const index = new Uint32Array(sections[2])
  const indirectBuffer = new Uint32Array(sections[3])
  const triangleHits = new Uint32Array(sections[4])
  const roots = sections.slice(5)
  const triangles = index.length / 3
  if (
    positions.length % 3 !== 0 ||
    normals.length !== positions.length ||
    index.length % 3 !== 0 ||
    positions.some((value) => !Number.isFinite(value)) ||
    normals.some((value) => !Number.isFinite(value)) ||
    index.some((value) => value >= positions.length / 3) ||
    indirectBuffer.length > triangles ||
    indirectBuffer.some((value) => value >= triangles) ||
    triangleHits.length !== triangles ||
    !Array.isArray(header.hitTable) ||
    triangleHits.some((value) => value >= header.hitTable.length)
  ) {
    throw new Error('Invalid spatial artifact geometry or triangle mapping')
  }
  const seenTriangles = new Uint8Array(triangles)
  for (const triangle of indirectBuffer) {
    if (seenTriangles[triangle]) {
      throw new Error('Duplicate spatial artifact triangle')
    }
    seenTriangles[triangle] = 1
  }
  if (
    !Array.isArray(header.groups) ||
    !Array.isArray(header.metadata?.meshMetadata) ||
    !Array.isArray(header.metadata?.semanticTags) ||
    header.hitTable.some(
      (hit) =>
        !hit || typeof hit.meshId !== 'string' || !hit.semantic || !Array.isArray(hit.semantic.tags)
    )
  ) {
    throw new Error('Invalid spatial artifact metadata')
  }
  for (const group of header.groups) {
    if (
      !Number.isInteger(group.start) ||
      !Number.isInteger(group.count) ||
      group.start < 0 ||
      group.count < 0 ||
      group.start % 3 !== 0 ||
      group.count % 3 !== 0 ||
      group.start + group.count > index.length
    ) {
      throw new Error('Invalid spatial artifact group')
    }
  }
  const includedTriangles = new Uint8Array(triangles)
  if (header.groups.length === 0) {
    includedTriangles.fill(1)
  } else {
    for (const group of header.groups) {
      includedTriangles.fill(1, group.start / 3, (group.start + group.count) / 3)
    }
  }
  if (includedTriangles.some((included, triangle) => included !== seenTriangles[triangle])) {
    throw new Error('Spatial artifact triangle mapping does not match groups')
  }
  for (const root of roots) {
    validateRoot(root, indirectBuffer.length)
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
  geometry.setIndex(new THREE.BufferAttribute(index, 1))
  geometry.groups = header.groups
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()
  return {
    source,
    geometry,
    bounds: geometry.boundingBox?.clone() ?? null,
    metadata: { ...header.metadata, source },
    triangleMetadata: Array.from(triangleHits, (id) => header.hitTable[id]),
    serializedBVH: { version: 1, roots, index, indirectBuffer },
  }
}

function copyAttribute(geometry: THREE.BufferGeometry, name: string): Float32Array {
  const attribute = geometry.getAttribute(name)
  if (!attribute || attribute.itemSize !== 3 || !(attribute.array instanceof Float32Array)) {
    throw new Error(`Unsupported spatial attribute: ${name}`)
  }
  const values = new Float32Array(attribute.count * 3)
  for (let i = 0; i < attribute.count; i++) {
    values.set([attribute.getX(i), attribute.getY(i), attribute.getZ(i)], i * 3)
  }
  return values
}

function validateRoot(root: ArrayBuffer, triangles: number): void {
  if (!root.byteLength || root.byteLength % 32 !== 0) {
    throw new Error('Invalid spatial BVH root size')
  }
  const view = new DataView(root)
  const nodeCount = root.byteLength / 32
  const pending = [0]
  const visited = new Uint8Array(nodeCount)
  let visitedCount = 0
  while (pending.length) {
    const node = pending.pop()!
    if (node >= nodeCount || visited[node]) {
      throw new Error('Invalid spatial BVH tree')
    }
    visited[node] = 1
    visitedCount++
    const offset = node * 32
    for (let axis = 0; axis < 3; axis++) {
      const min = view.getFloat32(offset + axis * 4, true)
      const max = view.getFloat32(offset + (axis + 3) * 4, true)
      if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
        throw new Error('Invalid spatial BVH bounds')
      }
    }
    if (view.getUint16(offset + 30, true) === 0xffff) {
      const start = view.getUint32(offset + 24, true)
      const count = view.getUint16(offset + 28, true)
      if (count === 0 || start + count > triangles) {
        throw new Error('Invalid spatial BVH leaf')
      }
    } else {
      const right = view.getUint32(offset + 24, true)
      if (right <= 1 || view.getUint32(offset + 28, true) > 2) {
        throw new Error('Invalid spatial BVH split')
      }
      pending.push(node + 1, node + right)
    }
  }
  if (visitedCount !== nodeCount) {
    throw new Error('Unreachable spatial BVH nodes')
  }
}
